import React, { useState } from 'react';
import MessageList from '../../components/Messaging/MessageList';
import MessageThread from '../../components/Messaging/MessageThread';
import NewConversationModal from '../../components/Messaging/NewConversationModal';
import { useQuery } from 'react-query';
import axios from 'axios';
import {
  UserPlusIcon,
  BellIcon,
  ArchiveBoxIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const Messaging = () => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [activeQuickAction, setActiveQuickAction] = useState(null);
  const [filterType, setFilterType] = useState('all'); // all, unread, archived

  // Récupérer les statistiques de messagerie
  const { data: messagingStats } = useQuery(
    'messaging-stats',
    async () => {
      const response = await axios.get('/api/messages/stats');
      return response.data;
    }
  );

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
  };

  const handleBackToList = () => {
    setSelectedConversation(null);
  };

  const handleQuickAction = (action) => {
    setActiveQuickAction(action);
    
    switch (action) {
      case 'new-conversation':
        setShowNewConversationModal(true);
        break;
      case 'unread-messages':
        setFilterType('unread');
        setSelectedConversation(null);
        break;
      case 'archived-conversations':
        setFilterType('archived');
        setSelectedConversation(null);
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text mb-2">
            Messagerie
          </h1>
          <p className="text-gray-600">
            Communiquez avec les entrepreneurs et investisseurs de la plateforme
          </p>
        </div>

        {/* Messaging Interface */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[600px]">
          {/* Conversations List */}
          <div className="lg:col-span-1">
            <MessageList 
              onSelectConversation={handleSelectConversation} 
              filterType={filterType}
            />
          </div>

          {/* Message Thread */}
          <div className="lg:col-span-2">
            <MessageThread 
              conversation={selectedConversation} 
              onBack={handleBackToList}
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text">Actions rapides</h2>
            {messagingStats && (
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <BellIcon className="h-4 w-4 mr-1 text-blue-500" />
                  {messagingStats.unreadCount} non lus
                </span>
                <span className="flex items-center">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1 text-green-500" />
                  {messagingStats.totalConversations} conversations
                </span>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button 
              onClick={() => handleQuickAction('new-conversation')}
              className={`flex items-center p-4 rounded-lg transition-colors ${
                activeQuickAction === 'new-conversation' 
                  ? 'bg-primary-100 border-2 border-primary-500' 
                  : 'bg-primary-50 hover:bg-primary-100'
              }`}
            >
              <div className="p-2 bg-primary-100 rounded-lg mr-3">
                <UserPlusIcon className="h-5 w-5 text-primary-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-text">Nouvelle conversation</p>
                <p className="text-sm text-gray-600">Contacter un utilisateur</p>
              </div>
            </button>
            
            <button 
              onClick={() => handleQuickAction('unread-messages')}
              className={`flex items-center p-4 rounded-lg transition-colors ${
                activeQuickAction === 'unread-messages' 
                  ? 'bg-blue-100 border-2 border-blue-500' 
                  : 'bg-blue-50 hover:bg-blue-100'
              }`}
            >
              <div className="p-2 bg-blue-100 rounded-lg mr-3">
                <BellIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-text">Messages non lus</p>
                <p className="text-sm text-gray-600">
                  {messagingStats?.unreadCount || 0} nouveaux messages
                </p>
              </div>
            </button>
            
            <button 
              onClick={() => handleQuickAction('archived-conversations')}
              className={`flex items-center p-4 rounded-lg transition-colors ${
                activeQuickAction === 'archived-conversations' 
                  ? 'bg-green-100 border-2 border-green-500' 
                  : 'bg-green-50 hover:bg-green-100'
              }`}
            >
              <div className="p-2 bg-green-100 rounded-lg mr-3">
                <ArchiveBoxIcon className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-text">Archives</p>
                <p className="text-sm text-gray-600">Conversations archivées</p>
              </div>
            </button>
          </div>
        </div>

        {/* Modal pour nouvelle conversation */}
        {showNewConversationModal && (
          <NewConversationModal
            isOpen={showNewConversationModal}
            onClose={() => {
              setShowNewConversationModal(false);
              setActiveQuickAction(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Messaging;
